import React, {
  createContext,
  useContext,
  useRef,
  useCallback,
  useState,
} from 'react';

interface QuestionRefsContextValue {
  registerRef: (questionId: string, element: HTMLDivElement | null) => void;
  scrollToQuestion: (questionId: string) => void;
  highlightQuestion: (questionId: string) => void;
  removeHighlight: (questionId: string) => void;
  setUnansweredQuestions: (questionIds: string[], hideText?: boolean) => void;
  clearUnansweredHighlight: (questionId: string) => void;
  unansweredQuestions: string[];
  setQuestionErrors: (errors: Record<string, string>) => void;
  clearQuestionError: (questionId: string) => void;
  clearAllErrors: () => void;
  questionErrors: Record<string, string>;
}

const QuestionRefsContext = createContext<QuestionRefsContextValue | null>(
  null
);

export const QuestionRefsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const questionRefsMap = useRef<Map<string, HTMLDivElement>>(new Map());
  const [unansweredQuestions, setUnansweredQuestionsState] = useState<string[]>(
    []
  );
  const [questionErrors, setQuestionErrorsState] = useState<Record<string, string>>(
    {}
  );

  const registerRef = useCallback(
    (questionId: string, element: HTMLDivElement | null) => {
      if (element) {
        questionRefsMap.current.set(questionId, element);
      } else {
        questionRefsMap.current.delete(questionId);
      }
    },
    []
  );

  const scrollToQuestion = useCallback((questionId: string) => {
    const element = questionRefsMap.current.get(questionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      console.warn('Question element not found:', questionId);
    }
  }, []);

  const highlightQuestion = useCallback((questionId: string) => {
    const element = questionRefsMap.current.get(questionId);
    if (element) {
      element.classList.add('highlighted-question');
    }
  }, []);

  const removeHighlight = useCallback((questionId: string) => {
    const element = questionRefsMap.current.get(questionId);
    if (element) {
      element.classList.remove('highlighted-question');
    }
  }, []);

  const setUnansweredQuestions = useCallback(
    (questionIds: string[], hideText?: boolean) => {
      // Remove unanswered-question class from all previously unanswered questions
      questionRefsMap.current.forEach((element, id) => {
        element.classList.remove('unanswered-question');
        element.classList.remove('unanswered-question-without-text');
      });

      // Add unanswered-question class after DOM updates
      requestAnimationFrame(() => {
        questionIds.forEach(questionId => {
          const element = questionRefsMap.current.get(questionId);
          if (element) {
            hideText
              ? element.classList.add('unanswered-question-without-text')
              : element.classList.add('unanswered-question');
          } else {
            console.warn('Element not found in map for question:', questionId);
          }
        });
      });

      setUnansweredQuestionsState(questionIds);
    },
    []
  );

  const clearUnansweredHighlight = useCallback((questionId: string) => {
    const element = questionRefsMap.current.get(questionId);
    if (element) {
      element.classList.remove('unanswered-question');
      element.classList.remove('unanswered-question-without-text');
    }
    // Also remove from state
    setUnansweredQuestionsState(prev => prev.filter(id => id !== questionId));
  }, []);

  const setQuestionErrors = useCallback(
    (errors: Record<string, string>) => {
      // Clear previous error classes
      questionRefsMap.current.forEach((element) => {
        element.classList.remove('question-error');
        element.removeAttribute('data-error-message');
        element.removeAttribute('title');
      });

      // Apply error classes and messages
      requestAnimationFrame(() => {
        Object.entries(errors).forEach(([questionId, errorMessage]) => {
          const element = questionRefsMap.current.get(questionId);
          if (element) {
            element.classList.add('question-error');
            element.setAttribute('data-error-message', errorMessage);
            element.setAttribute('title', errorMessage); // Tooltip on hover
          } else {
            console.warn('Element not found in map for question:', questionId);
          }
        });
      });

      setQuestionErrorsState(errors);
    },
    []
  );

  const clearQuestionError = useCallback((questionId: string) => {
    const element = questionRefsMap.current.get(questionId);
    if (element) {
      element.classList.remove('question-error');
      element.removeAttribute('data-error-message');
      element.removeAttribute('title');
    }
    setQuestionErrorsState(prev => {
      const newErrors = { ...prev };
      delete newErrors[questionId];
      return newErrors;
    });
  }, []);

  const clearAllErrors = useCallback(() => {
    questionRefsMap.current.forEach((element) => {
      element.classList.remove('question-error');
      element.removeAttribute('data-error-message');
      element.removeAttribute('title');
    });
    setQuestionErrorsState({});
  }, []);

  const value: QuestionRefsContextValue = {
    registerRef,
    scrollToQuestion,
    highlightQuestion,
    removeHighlight,
    setUnansweredQuestions,
    clearUnansweredHighlight,
    unansweredQuestions,
    setQuestionErrors,
    clearQuestionError,
    clearAllErrors,
    questionErrors,
  };

  return (
    <QuestionRefsContext.Provider value={value}>
      {children}
    </QuestionRefsContext.Provider>
  );
};

export const useQuestionRefs = () => {
  const context = useContext(QuestionRefsContext);
  if (!context) {
    throw new Error('useQuestionRefs must be used within QuestionRefsProvider');
  }
  return context;
};
